"use client";

import { cn } from "@/lib/utils";
import {
  getCircleActionVariantStyle,
  resolveCircleActionVariant,
  type CircleActionVariant
} from "../circle-action-variants";
import {
  CircleActionParticleBurst,
  type CircleActionParticleBurstProps,
} from "./circle-action-particle-burst";

const BLOB_POSITIONS = [
  "left-[6%] top-[18%]",
  "right-[4%] top-[16%]",
  "left-[10%] bottom-[16%]",
  "right-[8%] bottom-[18%]",
] as const;

export interface CircleActionHoverFxProps {
  variant?: CircleActionVariant | string | null;
  /** @deprecated Usar `variant`. */
  tone?: CircleActionVariant | string | null;
  size?: "sm" | "md";
}

export function CircleActionHoverFx({
  variant: variantProp,
  tone,
  size = "sm",
}: CircleActionHoverFxProps) {
  const resolved = resolveCircleActionVariant(variantProp ?? tone ?? undefined);
  const { fx } = getCircleActionVariantStyle(resolved);
  const blobSize = size === "sm" ? "h-8 w-8" : "h-10 w-10";
  const layerInset = size === "sm" ? "-inset-6" : "-inset-7";

  return (
    <>
    <span
      className={cn(
        "cab-fx-layer pointer-events-none absolute z-[1] overflow-visible",
        layerInset
      )}
      aria-hidden
    >
      <span
        className="cab-fx-bloom"
        style={{ background: fx.bloom }}
      />
      <span className="cab-fx-orbit motion-safe:animate-cab-orbit">
        {BLOB_POSITIONS.map((position, index) => (
          <span
            key={position}
            className={cn(
              "cab-fx-blob absolute rounded-full blur-lg",
              blobSize,
              position,
              index % 2 === 0
                ? "motion-safe:animate-cab-blob-pulse-a"
                : "motion-safe:animate-cab-blob-pulse-b"
            )}
            style={{
              background: `radial-gradient(circle, rgba(${fx.blobs[index]},0.52) 0%, rgba(${fx.blobs[index]},0.22) 45%, transparent 72%)`,
            }}
          />
        ))}
      </span>
    </span>
    <style jsx global>{`
      .cab-fx-layer {
        opacity: 0;
        overflow: visible;
        transition: opacity 420ms cubic-bezier(0.16,1,0.3,1);
      }
      .cab-fx-host:hover .cab-fx-layer { opacity: 0.72; }
      .cab-fx-host[data-active="true"] .cab-fx-layer { opacity: 0.38; }
      .cab-fx-host[data-active="true"]:not(:hover) .cab-fx-layer { opacity: 0.38; }
      .cab-fx-bloom {
        position: absolute;
        inset: 12%;
        border-radius: 9999px;
        filter: blur(12px);
        opacity: 0;
        background: radial-gradient(circle at 30% 40%, rgba(255,255,255,0.35) 0%, rgba(125,211,252,0.28) 28%, rgba(99,102,241,0.18) 52%, transparent 72%);
        transition: opacity 480ms ease, transform 520ms cubic-bezier(0.16,1,0.3,1);
      }
      .cab-fx-host:hover .cab-fx-bloom { opacity: 0.5; transform: scale(1.06); }
      .cab-fx-orbit { position: absolute; inset: 0; }
      .cab-fx-blob { opacity: 0; transition: opacity 400ms ease; }
      .cab-fx-host:hover .cab-fx-blob { opacity: 0.55; }
      .cab-fx-particles { pointer-events: none; opacity: 0; }
      .cab-fx-particle {
        left: 0;
        top: 0;
        width: var(--cab-p-size, 4px);
        height: var(--cab-p-size, 4px);
        background-color: var(--cab-particle-color, #67e8f9);
        opacity: 0;
        box-shadow: none;
        animation: none;
      }
      @keyframes cab-orbit { to { transform: rotate(360deg); } }
      @keyframes cab-blob-pulse-a {
        0%, 100% { transform: scale(0.92); opacity: 0.4; }
        50% { transform: scale(1.06); opacity: 0.55; }
      }
      @keyframes cab-blob-pulse-b {
        0%, 100% { transform: scale(1.04); opacity: 0.48; }
        50% { transform: scale(0.9); opacity: 0.38; }
      }
      @keyframes cab-particle-burst {
        0% { opacity: 1; transform: translate(-50%, -50%) scale(0.4); }
        100% {
          opacity: 0;
          transform: translate(calc(-50% + var(--cab-px, 24px)), calc(-50% + var(--cab-py, 0px))) scale(2.2);
        }
      }
      .motion-safe\:animate-cab-orbit { animation: cab-orbit 18s linear infinite; }
      .motion-safe\:animate-cab-blob-pulse-a { animation: cab-blob-pulse-a 3.6s ease-in-out infinite; }
      .motion-safe\:animate-cab-blob-pulse-b { animation: cab-blob-pulse-b 4.2s ease-in-out infinite; }
      @media (prefers-reduced-motion: reduce) {
        .motion-safe\:animate-cab-orbit,
        .motion-safe\:animate-cab-particle-burst,
        .motion-safe\:animate-cab-blob-pulse-a,
        .motion-safe\:animate-cab-blob-pulse-b { animation: none !important; }
      }
    `}</style>
    </>
  );
}

export type { CircleActionParticleBurstProps };
export { CircleActionParticleBurst };
